import React, { useEffect, useState } from 'react';
import { useWallet } from '../context/WalletContext';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import { useWallets } from '../hooks/useWallets';
import walletService from '../services/walletService';
import TransactionsList from '../components/wallet/TransactionsList';
import PaymentMethodsList from '../components/wallet/PaymentMethodsList';
import DepositDialog from '../components/wallet/DepositDialog';
import AddPaymentMethodForm from '../components/wallet/AddPaymentMethodForm';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';
import { Dialog } from '../components/ui/radix/Dialog';

type Tab = 'transactions' | 'methods';

const WalletPage: React.FC = () => {
  const {
    balance,
    addFunds,
    transactions,
    paymentMethods,
    removePaymentMethod,
    setDefaultPaymentMethod,
    fetchTransactions,
    isLoading: isWalletLoading,
    isError: isWalletError,
  } = useWallet();
  const { refetch: refetchWallets } = useWallets();
  const { refetch: refetchPaymentMethods } = usePaymentMethods();

  const [tab, setTab] = useState<Tab>('transactions');
  const [openDeposit, setOpenDeposit] = useState(false);
  const [openWithdraw, setOpenWithdraw] = useState(false);
  const [openAddPayment, setOpenAddPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingTransactionId, setDownloadingTransactionId] = useState<string | null>(null);

  useEffect(() => {
    if (tab === 'transactions') fetchTransactions();
    if (tab === 'methods') refetchPaymentMethods();
    // fetchTransactions / refetchPaymentMethods are recreated every render
    // by their providers (WalletContext value object, React Query hook). Including
    // them here causes an infinite request loop. Re-run only on tab change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleDeposit = async (amount: number, methodId: string) => {
    setProcessing(true);
    setError(null);
    try {
      await addFunds(amount, methodId);
      setSuccess(`Successfully added $${amount.toFixed(2)} to your wallet`);
      setTimeout(() => {
        setOpenDeposit(false);
        setSuccess(null);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deposit failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetchWallets();
      if (tab === 'transactions') await fetchTransactions?.();
      if (tab === 'methods') await refetchPaymentMethods();
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddPaymentSuccess = (message: string) => {
    setSuccess(message);
    refetchPaymentMethods();
    setTimeout(() => {
      setOpenAddPayment(false);
      setSuccess(null);
    }, 2000);
  };

  const handleDownloadReceipt = async (transactionId: string, format: 'pdf' | 'xlsx' = 'xlsx') => {
    setDownloadingTransactionId(transactionId);
    setError(null);
    try {
      const blob = await walletService.downloadTransactionReceipt(transactionId, format);
      const mimeType =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${transactionId}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess(`Receipt downloaded as ${format.toUpperCase()}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadingTransactionId(null);
    }
  };

  const transformedPaymentMethods =
    paymentMethods?.map((m) => ({
      id: m.id,
      type: m.type,
      name: m.name,
      last4: m.last4,
      isDefault: m.isDefault,
    })) ?? [];

  const transformedTransactions =
    transactions?.map((t) => ({
      id: t.id?.toString() || Math.random().toString(),
      type: t.type,
      amount: t.amount,
      date: t.date || new Date().toISOString(),
      description: t.description || 'Transaction',
      status: t.status as 'completed' | 'pending' | 'failed',
      reference: t.reference,
    })) ?? [];

  const balanceAmount = typeof balance === 'number' ? balance : 0;

  return (
    <Page width="wide">
      <PageHead>
        <div>
          <PageTitle>Wallet</PageTitle>
          <PageSub>Pay for shipments from your balance. Auto-convert enabled.</PageSub>
        </div>
        <div className="wallet-page-actions" style={{ display: 'flex', gap: 8 }}>
          <Button onClick={handleRefresh} disabled={refreshing} title="Refresh wallet">
            <Icon name="refresh" size={14} />
            <span>Refresh</span>
          </Button>
          <Button variant="blue" onClick={() => setOpenDeposit(true)}>
            <Icon name="plus" size={14} />
            <span>Add funds</span>
          </Button>
        </div>
      </PageHead>

      {isWalletError && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">
            <Icon name="alertCircle" size={16} />
            <span>There was an error loading your wallet data.</span>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={handleRefresh}
              style={{ marginInlineStart: 'auto' }}
            >
              Retry
            </button>
          </Callout>
        </div>
      )}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">
            <Icon name="alertCircle" size={16} />
            <span>{error}</span>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto' }}
            >
              <Icon name="x" size={12} />
            </button>
          </Callout>
        </div>
      )}
      {success && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="ok">
            <Icon name="checkCircle" size={16} />
            <span>{success}</span>
          </Callout>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20 }} className="details-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card
            className="wallet-balance-card"
            style={{
              padding: 28,
              background: 'linear-gradient(135deg, var(--brand-navy), var(--brand-ink))',
              color: '#fff',
              border: 'none',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="mono" style={{ fontSize: 11, letterSpacing: '0.1em', color: '#9a9fb8' }}>
                  BALANCE · ILS
                </div>
                <div
                  className="mono wallet-balance-amount"
                  style={{ fontSize: 48, fontWeight: 600, letterSpacing: '-0.02em', marginTop: 4 }}
                >
                  ₪{balanceAmount.toFixed(2)}
                </div>
              </div>
              <Icon name="wallet" size={28} color="rgba(255,255,255,0.3)" />
            </div>
            <div className="wallet-balance-actions" style={{ display: 'flex', gap: 8, marginTop: 24 }}>
              <button
                type="button"
                className="btn btn-sm"
                style={{ background: 'var(--brand-cyan)', color: 'var(--brand-navy)' }}
                onClick={() => setOpenDeposit(true)}
              >
                <Icon name="plus" size={14} /> Top up
              </button>
              <button
                type="button"
                className="btn btn-sm"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
                onClick={() => setOpenWithdraw(true)}
              >
                <Icon name="download" size={13} /> Withdraw
              </button>
            </div>
          </Card>

          <Card>
            <div className="card-header wallet-tabs-header">
              <div className="tabs wallet-tabs" style={{ border: 'none' }}>
                <button
                  className={`tab ${tab === 'transactions' ? 'active' : ''}`}
                  onClick={() => setTab('transactions')}
                >
                  Transactions
                </button>
                <button
                  className={`tab ${tab === 'methods' ? 'active' : ''}`}
                  onClick={() => setTab('methods')}
                >
                  Payment methods
                </button>
              </div>
            </div>
            <div className="wallet-tab-body" style={{ padding: 16 }}>
              {tab === 'transactions' && (
                <TransactionsList
                  transactions={transformedTransactions}
                  isLoading={isWalletLoading}
                  onRefresh={fetchTransactions}
                  onDownloadReceipt={handleDownloadReceipt}
                  downloadingTransactionId={downloadingTransactionId}
                />
              )}
              {tab === 'methods' && (
                <PaymentMethodsList
                  paymentMethods={transformedPaymentMethods}
                  onAddNew={() => setOpenAddPayment(true)}
                  onRemove={removePaymentMethod}
                  onSetDefault={setDefaultPaymentMethod}
                  isLoading={isWalletLoading}
                />
              )}
            </div>
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card pad>
            <h3 style={{ fontSize: 15, marginBottom: 12 }}>Quick actions</h3>
            <Button block variant="secondary" onClick={() => setOpenDeposit(true)}>
              <Icon name="plus" size={14} /> Add funds
            </Button>
            <div style={{ marginTop: 8 }}>
              <Button block variant="ghost" onClick={() => setOpenAddPayment(true)}>
                <Icon name="card" size={14} /> Add payment method
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <DepositDialog
        open={openDeposit}
        onClose={() => setOpenDeposit(false)}
        onDeposit={handleDeposit}
        paymentMethods={transformedPaymentMethods}
        processing={processing}
        error={error}
        success={success}
      />

      <Dialog
        open={openWithdraw}
        onOpenChange={setOpenWithdraw}
        title="Withdraw funds"
        maxWidth={420}
      >
        <Callout tone="info">
          <Icon name="info" size={16} />
          <span>
            Withdrawal functionality will be available soon. For now, you can only add funds to your wallet.
          </span>
        </Callout>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button onClick={() => setOpenWithdraw(false)}>Close</Button>
        </div>
      </Dialog>

      <Dialog
        open={openAddPayment}
        onOpenChange={setOpenAddPayment}
        title="Add payment method"
        maxWidth={600}
      >
        <AddPaymentMethodForm
          onSuccess={handleAddPaymentSuccess}
          onError={setError}
          onClose={() => setOpenAddPayment(false)}
          existingMethodsCount={transformedPaymentMethods.length}
        />
      </Dialog>
    </Page>
  );
};

export default WalletPage;
